import type { VenueData } from "@/types/venue";

const MAX_SLUG_LENGTH = 96;

type VenueSeoInput = Pick<VenueData, "id" | "name" | "city" | "state" | "zipCode" | "address">;

export function buildVenueSeoPath(venue: VenueSeoInput): string {
  const venueId = Number(venue.id);
  if (!Number.isFinite(venueId) || venueId <= 0) {
    return "/venues";
  }

  const slug = buildVenueSlug(venue);
  return slug ? `/venues/${venueId}-${slug}` : `/venues/${venueId}`;
}

export function parseVenueSeoParam(rawParam: string): { venueId: number; slug: string } | null {
  let normalized = "";
  try {
    normalized = decodeURIComponent(rawParam ?? "");
  } catch {
    normalized = rawParam ?? "";
  }
  normalized = normalized.trim().toLowerCase();
  const match = normalized.match(/^(\d+)(?:-([a-z0-9-]+))?$/);
  if (!match) return null;

  const venueId = Number(match[1]);
  if (!Number.isFinite(venueId) || venueId <= 0) return null;

  return {
    venueId,
    slug: match[2] ?? "",
  };
}

export function buildVenueSlug(venue: Pick<VenueData, "name" | "city" | "state" | "zipCode" | "address">): string {
  const locationParts = [venue.city, venue.state, venue.zipCode]
    .map((value) => slugify(value ?? ""))
    .filter((value) => value.length > 0);

  if (locationParts.length === 0) {
    const fallbackLocation = slugify(firstAddressSegment(venue.address ?? ""));
    if (fallbackLocation) {
      locationParts.push(fallbackLocation);
    }
  }

  const namePart = slugify(venue.name ?? "");
  const joined = [...locationParts, namePart].filter((value) => value.length > 0).join("-");
  return clampSlugLength(joined, MAX_SLUG_LENGTH);
}

function firstAddressSegment(address: string): string {
  return address.split(",")[0]?.trim() ?? "";
}

function slugify(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

function clampSlugLength(slug: string, max: number): string {
  if (slug.length <= max) return slug;

  const sliced = slug.slice(0, max).replace(/-+$/g, "");
  const breakpoint = sliced.lastIndexOf("-");

  if (breakpoint >= 24) {
    return sliced.slice(0, breakpoint);
  }

  return sliced;
}
